import { gql } from '@src/lib/gql'

/**
 * Advisor-side Opportunities queries (supply view).
 *
 * Note: collection `id` is deliberately not selected. Root collections resolve
 * `id: null` in startups3 while the schema declares `ID!`, so selecting it
 * errors. Leaving it out also keeps the aliased counts query below un-normalized
 * in the Apollo cache, so the count aliases stay distinct.
 */

const OPPORTUNITY_FOUNDER_SELECTION = `
  id
  userId
  fullName
  displayName
  slug
  pictureUrl
  title
  shortBio
`

export const ADVISOR_INVITED_ENGAGEMENTS = gql`
  query AdvisorInvitedEngagements($pageSize: Int, $page: Int) {
    advisorEngagements(role: "ADVISOR", statusKeys: ["invited"], pageSize: $pageSize, page: $page) {
      items {
        id
        advisorEngagementId
        founderUserId
        categoryKey
        invitedAt
        whyText
        founderUser {
          ${OPPORTUNITY_FOUNDER_SELECTION}
        }
        category {
          id
          key
          title
        }
        company {
          id
          name
          elevatorPitch
        }
      }
      pagination {
        itemCount
        hasNextPage
        page
        pageCount
      }
    }
  }
`

export const ADVISOR_SEAT_APPLICATIONS = gql`
  query AdvisorSeatApplications($pageSize: Int, $page: Int, $statusKeys: [String]) {
    advisorSeatApplications(pageSize: $pageSize, page: $page, statusKeys: $statusKeys) {
      items {
        id
        groupCommitmentId
        groupRequestId
        title
        bodyAsMarkdown
        submittedAt
        createdAt
        acceptedAt
        rejectedAt
        groupRequest {
          id
          groupRequestId
          title
          needExplanation
          groupRequestTypeKey
          requestingUser {
            ${OPPORTUNITY_FOUNDER_SELECTION}
          }
          requestingCompany {
            id
            name
            elevatorPitch
          }
          currentUserOpenSeatMatch {
            id
            userMatchId
          }
        }
      }
      pagination {
        itemCount
        hasNextPage
        page
        pageCount
      }
    }
  }
`

export const ADVISOR_ACTIVE_ENGAGEMENTS = gql`
  query AdvisorActiveEngagements($pageSize: Int, $page: Int) {
    advisorEngagements(role: "ADVISOR", statusKeys: ["active"], pageSize: $pageSize, page: $page) {
      items {
        id
        advisorEngagementId
        categoryKey
        activatedAt
        founderUser {
          ${OPPORTUNITY_FOUNDER_SELECTION}
        }
        advisorUser {
          id
          userId
        }
        category {
          id
          key
          title
        }
        company {
          id
          name
        }
      }
      pagination {
        itemCount
        hasNextPage
        page
        pageCount
      }
    }
  }
`

/**
 * Badge counts for the Opportunities sub-navigation. `pageSize: 1` keeps this
 * cheap — only `pagination.itemCount` is read.
 */
export const ADVISOR_OPPORTUNITY_COUNTS = gql`
  query AdvisorOpportunityCounts {
    invited: advisorEngagements(role: "ADVISOR", statusKeys: ["invited"], pageSize: 1) {
      pagination {
        itemCount
      }
    }
    active: advisorEngagements(role: "ADVISOR", statusKeys: ["active"], pageSize: 1) {
      pagination {
        itemCount
      }
    }
    matched: advisorMatchedOpenSeats(pageSize: 1, page: 1) {
      pagination {
        itemCount
      }
    }
    applied: advisorSeatApplications(statusKeys: ["pending"], pageSize: 1) {
      pagination {
        itemCount
      }
    }
  }
`
